import React, {useContext, useState, useEffect} from "react";
import PropTypes from "prop-types";
import {
    makeStyles,
    Grid,
    Typography,
    Divider,
    Snackbar,
    Box,
    TextField,
    Avatar,
    CircularProgress
} from "@material-ui/core";
import Skeleton from "@material-ui/lab/Skeleton";
import CommonFileDouble2 from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/files-folders/common-files/common-file-double-2.svg";
import EmailActionUnread from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/emails/email-actions/email-action-unread.svg";
import Phone from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/phones-mobile-devices/phone/phone.svg";
import SendEmail from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/emails/send-email/send-email.svg";
import {CopyToClipboard} from "react-copy-to-clipboard";
import clsx from "clsx";
import {useI18N} from "../../../common/common";
import {useBreakpoints, useReservation} from "../../../hooks";
import {CustomButton, CustomIconButton, StreamlineIcon} from "@hapinest/theme";
import {toLowerCaseIfNotGerman} from "../../../common/string";
import {LocaleContext} from "../../../store/providers/LocaleProvider";
import {retrieveContentfulImage} from "../../../common/image";
import CustomSnackbar from "../../widget/alerts/CustomSnackbar";
import {useAnalytics} from "../../../store/providers/AnalyticsProvider";

const useStyles = makeStyles((theme) => ({
    wrapper: {
        margin: theme.spacing(1, 0, 1, 1),
        position: "relative",
    },
    buttonProgress: {
        color: theme.palette.grey[200],
        position: "absolute",
        top: "50%",
        left: "50%",
        marginTop: -12,
        marginLeft: -12,
    },
    inlineIcon: {
        margin: theme.spacing(-0.5),
        verticalAlign: "middle"
    },
    inlineLabel: {
        display: "inline-block",
        verticalAlign: "sub",
        marginLeft: theme.spacing(1.5)
    },
    divider: {
        backgroundColor: theme.palette.borders.line.light,
        margin: theme.spacing(4, 0)
    },
    floatRight: {
        float: "right"
    },
    mt2: {
        marginTop: theme.spacing(2)
    },
    mb1: {
        marginBottom: theme.spacing(1)
    },
    mb2: {
        marginBottom: theme.spacing(2)
    },
    mb3: {
        marginBottom: theme.spacing(3)
    },
    mb4: {
        marginBottom: theme.spacing(4)
    },
    avatar: {
        width: theme.spacing(6),
        height: theme.spacing(6),
        marginBottom: theme.spacing(0.5),
    },
    tip: {
        backgroundColor: theme.palette.backgrounds.secondary.lighter,
        border: "1px solid #D8D5D0",
        borderRadius: theme.spacing(0.5),
        marginTop: theme.spacing(2),
        padding: theme.spacing(1),
        maxWidth: "55%"
    },
    tip_text: {
        fontStyle: "italic",
        fontWeight: "normal",
        fontSize: theme.spacing(2),
        lineHeight: "22.4px"
    },
    bgWhite: {
        background: theme.palette.grey[0]
    }
}));

const ReservationContact = ({reservationId, resort, isReservationCancelled}) => {
    const {messageToResort, setMessageToResort, sendMessageToResort, messageSendingStatus, messageStatuses} = useReservation();

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen, isSmallScreen} = useBreakpoints();
    const {sendArrivalCardEvent, eventNames} = useAnalytics();

    const [localeState] = useContext(LocaleContext);
    const [showSuccessSnacbar, setShowSuccessSnackbar] = useState(false);
    const [showFailureSnacbar, setShowFailureSnackbar] = useState(false);

    const trackArrivalCardEvent = (eventName, extraData) => {
        sendArrivalCardEvent(eventName, { ...extraData }, resort);
    };

    const handleMessageToResortChange = (e) => {
        setMessageToResort(e.target.value);
    };

    const handleOpenSnackbar = () => {
        setOpenSnackbar(true);
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === "clickaway") {
            return;
        }
        setOpenSnackbar(false);
    };

    const handleCopy = (type) => {
        handleOpenSnackbar();
        // type passed is the event name for tracking
        trackArrivalCardEvent(type);
    };

    const handleSendMessageToResort = () => {
        sendMessageToResort(reservationId, resort.resortContactDetails?.host?.firstAndLastName, resort.resortContactDetails?.resortEmailAddress);
    };

    useEffect(() => {
        if (messageSendingStatus === messageStatuses.SUCCEEDED) {
            trackArrivalCardEvent(eventNames.CONTACT_HOST);
            setShowSuccessSnackbar(true);
        } else if (messageSendingStatus === messageStatuses.FAILED){
            setShowFailureSnackbar(true);
        } else {
            setShowSuccessSnackbar(false);
            setShowFailureSnackbar(false);
        }
    }, [messageSendingStatus]);

    return (
        <>
            <Typography
                variant="h3"
                className={classes.mb3}
            >
                {!resort ? <Skeleton/> : i18n("view.reservations.contact_your_host", {
                    gender: resort?.resortContactDetails?.host?.gender ? "female" : "male",
                    contactRole: toLowerCaseIfNotGerman(localeState?.locale, resort?.resortContactDetails?.host?.role)
                })}
            </Typography>
            {!resort?.isPartner &&
                <Grid
                    container
                    spacing={1}
                    alignItems="center"
                    wrap="nowrap"
                >
                    <Grid
                        item
                    >
                        <Avatar
                            className={classes.avatar}
                            src={retrieveContentfulImage(resort?.resortContactDetails?.host?.picture?.url)}
                        />
                    </Grid>
                    <Grid
                        item
                        container
                        direction="column"
                    >
                        <Typography
                            variant="h6"
                            component="div"
                            className={classes.mb1}
                        >
                            {!resort ? <Skeleton/> : resort.resortContactDetails?.host?.firstAndLastName}
                        </Typography>
                        <Typography
                            variant="body1"
                            component="div"
                        >
                            {!resort ? <Skeleton/> : i18n("view.reservations.your_host_in", {"contactRole": resort.resortContactDetails?.host?.role, "resortName": resort?.resortName})}
                        </Typography>
                    </Grid>
                </Grid>
            }
            {!resort?.isPartner && !isReservationCancelled &&
                <Box
                    className={classes.tip}
                >
                    <Typography
                        className={classes.tip_text}
                    >
                        {i18n("view.reservations.any_special_requests")}
                    </Typography>
                    <Typography
                        className={classes.tip_text}
                    >
                        {i18n("view.reservations.feel_free_contact")}
                    </Typography>
                </Box>
            }

            {!resort?.isPartner &&
                <Divider
                    className={clsx(classes.divider, classes.mt2, classes.mb4)}
                />
            }

            {!resort?.isPartner && (
                <>
                    {!resort ? (
                        <Skeleton
                            variant="rect"
                            height={118}
                        />
                    ) : (
                        <Box
                            className="no-print"
                        >
                            <TextField
                                fullWidth
                                multiline
                                placeholder={i18n("common.label.type_your_message")}
                                rows={4}
                                value={messageToResort}
                                variant="outlined"
                                onChange={handleMessageToResortChange}
                                disabled={messageSendingStatus === messageStatuses.SENDING}
                                className={classes.bgWhite}
                            />
                            <Box
                                mt={1}
                                display="flex"
                                justifyContent="flex-end"
                            >
                                {(isExtraSmallScreen() || isSmallScreen()) ? (
                                    <div className={classes.wrapper}>
                                        <CustomIconButton
                                            variant="contained"
                                            className={classes.floatRight}
                                            disabled={messageSendingStatus === messageStatuses.SENDING || messageToResort === ""}
                                            onClick={handleSendMessageToResort}
                                        >
                                            <StreamlineIcon><SendEmail/></StreamlineIcon>
                                        </CustomIconButton>
                                        {(messageSendingStatus === messageStatuses.SENDING) && (
                                            <CircularProgress
                                                size={24}
                                                className={classes.buttonProgress}
                                            />
                                        )}
                                    </div>
                                ) : (
                                    <div className={classes.wrapper}>
                                        <CustomButton
                                            variant="contained"
                                            size="medium"
                                            endIcon={<StreamlineIcon><SendEmail/></StreamlineIcon>}
                                            disabled={messageSendingStatus === messageStatuses.SENDING || messageToResort === ""}
                                            onClick={handleSendMessageToResort}
                                        >
                                            {i18n("common.label.send")}
                                        </CustomButton>
                                        {(messageSendingStatus === messageStatuses.SENDING) && (
                                            <CircularProgress
                                                size={24}
                                                className={classes.buttonProgress}
                                            />
                                        )}
                                    </div>
                                )}
                            </Box>
                        </Box>
                    )}

                    <Divider
                        className={clsx(classes.divider, classes.mt2, classes.mb2, "no-print")}
                    />
                </>
            )}

            {!resort ? (
                <Typography
                    variant="body1"
                >
                    <Skeleton/>
                </Typography>
            ) : (
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justify="space-between"
                >
                    <Grid
                        item
                    >
                        <StreamlineIcon
                            className={clsx(classes.inlineIcon)}
                        >
                            <EmailActionUnread/>
                        </StreamlineIcon>
                        <Typography
                            variant="body1"
                            className={classes.inlineLabel}
                        >
                            {resort.resortContactDetails?.resortEmailAddress}
                        </Typography>
                    </Grid>
                    <Grid item>
                        <Box displayPrint="none">
                            <CopyToClipboard
                                text={resort?.resortContactDetails?.resortEmailAddress}
                                onCopy={() => handleCopy(eventNames.COPY_HOST_EMAIL)}
                            >
                                {(isExtraSmallScreen() || isSmallScreen()) ? (
                                    <CustomIconButton
                                        variant={"outlined"}
                                        color={"black"}
                                        className={classes.floatRight}
                                    >
                                        <StreamlineIcon><CommonFileDouble2/></StreamlineIcon>
                                    </CustomIconButton>
                                ) : (
                                    <CustomButton
                                        variant={"outlined"}
                                        color={"black"}
                                        size={"medium"}
                                        startIcon={<StreamlineIcon><CommonFileDouble2/></StreamlineIcon>}
                                    >
                                        {i18n("common.label.copy")}
                                    </CustomButton>
                                )}
                            </CopyToClipboard>
                        </Box>
                    </Grid>
                </Grid>
            )}

            <Divider
                className={clsx(classes.divider, classes.mt2, classes.mb2)}
            />

            {!resort ? (
                <Typography
                    variant="body1"
                >
                    <Skeleton/>
                </Typography>
            ) : (
                <Grid
                    container
                    direction="row"
                    alignItems="center"
                    justify="space-between"
                >
                    <Grid item>
                        <StreamlineIcon
                            className={clsx(classes.inlineIcon)}
                        >
                            <Phone/>
                        </StreamlineIcon>
                        <Typography
                            variant="body1"
                            className={classes.inlineLabel}
                        >
                            {!resort ? <Skeleton/> : resort.resortContactDetails?.resortPhoneNumber}
                        </Typography>
                    </Grid>
                    {!isReservationCancelled &&
                        <Grid item>
                            <Box displayPrint="none">
                                <CopyToClipboard
                                    text={resort?.resortContactDetails?.resortPhoneNumber}
                                    onCopy={() => handleCopy(eventNames.COPY_HOST_PHONE)}
                                >
                                    {(isExtraSmallScreen() || isSmallScreen()) ? (
                                        <CustomIconButton
                                            variant={"outlined"}
                                            color={"black"}
                                            className={classes.floatRight}
                                        >
                                            <StreamlineIcon><CommonFileDouble2/></StreamlineIcon>
                                        </CustomIconButton>
                                    ) : (
                                        <CustomButton
                                            variant={"outlined"}
                                            color={"black"}
                                            size={"medium"}
                                            startIcon={<StreamlineIcon><CommonFileDouble2/></StreamlineIcon>}
                                        >
                                            {i18n("common.label.copy")}
                                        </CustomButton>
                                    )}
                                </CopyToClipboard>
                            </Box>
                        </Grid>
                    }
                </Grid>
            )}

            <Snackbar
                anchorOrigin={{
                    vertical: "bottom",
                    horizontal: "center",
                }}
                open={openSnackbar}
                autoHideDuration={3000}
                onClose={handleCloseSnackbar}
                message={i18n("common.label.copied") + "!"}
            />
            {/* Success sending message snackbar */}
            <CustomSnackbar
                open={showSuccessSnacbar}
                type="success"
                withIcon
                closeIcon
                vertical="top"
                horizontal="center"
                animationDirection="down"
                message={i18n("view.reservations.message_sent")}
            />
            {/* Failed sending message snackbar */}
            <CustomSnackbar
                open={showFailureSnacbar}
                type="warning"
                withIcon
                closeIcon
                vertical="top"
                horizontal="center"
                animationDirection="down"
                message={i18n("view.reservations.message_failed")}
            />
        </>
    );
};

ReservationContact.propTypes = {
    resort: PropTypes.any,
    reservationId: PropTypes.string,
    isReservationCancelled: PropTypes.bool,
};

export default ReservationContact;
